import axiosInstance from '../auth/axiosInstance';
import { assertApiSuccess, getApiErrorMessage } from '../types/api';
import { normalizeCapabilityFromApi } from '../utils/capabilityApiNormalize';
import { mapQuestionsForSave } from '../utils/subCapabilityQuestionUtils';

type GeneratedCapabilityApiDraft = {
  frameworkId?: number;
  sourceFrameworkName?: string;
  referenceCompanyName?: string;
  description?: string;
  frameworkType?: string;
  businessTypeId?: number;
  businessTypeName?: string;
  attachmentFileNames?: string[];
  urls?: string[];
  capabilities?: Record<string, unknown>[];
};

export const generateCapabilitiesFromFramework = async (frameworkId) => {
  try {
    const { data } = await axiosInstance.post(
      `/CapabilityGeneration/generateFromFramework/${frameworkId}`,
      undefined,
      { timeout: 600_000 }
    );

    const draft = assertApiSuccess<GeneratedCapabilityApiDraft>(data);
    const capabilities = Array.isArray(draft?.capabilities) ? draft.capabilities : [];
    if (!capabilities.length) {
      throw new Error('Generator returned no capability drafts.');
    }

    return {
      ...draft,
      capabilities: capabilities.map(normalizeCapabilityFromApi),
      savePayload: capabilities,
    };
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

export const createGeneratedCapabilities = async (capabilityPayload) => {
  try {
    const { data } = await axiosInstance.post(
      '/CapabilityGeneration/createGeneratedCapabilities',
      capabilityPayload
    );

    return assertApiSuccess(data);
  } catch (error) {
    throw new Error(getApiErrorMessage(error));
  }
};

const blankToNull = (value) => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed.length ? trimmed : null;
};

/** Maps the editable generate-review draft into the create API payload. */
export const buildGeneratedCapabilitySavePayload = (draft, businessTypeId) => {
  const typeId = Number(businessTypeId);
  if (!typeId) return [];

  return (draft?.capabilities || [])
    .filter((cap) => String(cap?.capabilityName || cap?.name || '').trim())
    .map((cap) => {
      const namedSubs = (cap.subCapabilities || []).filter((sub) =>
        String(sub?.subCapabilityName || sub?.name || '').trim()
      );
      const hasSubs = namedSubs.length > 0;

      return {
        id: 0,
        businessTypeId: typeId,
        frameworkId: cap.frameworkId || draft.frameworkId || null,
        capabilityId: null,
        name: String(cap.capabilityName || cap.name || '').trim(),
        iconURL: cap.capabilityIconUrl || cap.iconURL || '',
        description: cap.capabilityDescription || cap.description || '',
        isActive: cap.isActive !== false,
        currentStateQuestions: hasSubs ? [] : mapQuestionsForSave(cap.currentStateQuestions),
        futureStateQuestions: hasSubs ? [] : mapQuestionsForSave(cap.futureStateQuestions),
        currentStateCommentPlaceholder: hasSubs
          ? null
          : blankToNull(cap.currentStateCommentPlaceholder),
        futureStateCommentPlaceholder: hasSubs
          ? null
          : blankToNull(cap.futureStateCommentPlaceholder),
        maturityOptionsJson: hasSubs ? null : blankToNull(cap.maturityOptionsJson),
        subCapabilities: namedSubs.map((sub) => ({
          id: 0,
          capabilityId: null,
          businessTypeId: typeId,
          frameworkId: null,
          name: String(sub.subCapabilityName || sub.name || '').trim(),
          description: sub.subCapabilityDescription || sub.description || '',
          iconURL: '',
          isActive: sub.isActive !== false,
          currentStateCommentPlaceholder: blankToNull(sub.currentStateCommentPlaceholder),
          futureStateCommentPlaceholder: blankToNull(sub.futureStateCommentPlaceholder),
          currentStateQuestions: mapQuestionsForSave(sub.currentStateQuestions),
          futureStateQuestions: mapQuestionsForSave(sub.futureStateQuestions),
          maturityOptionsJson: blankToNull(sub.maturityOptionsJson),
          subCapabilities: [],
        })),
      };
    });
};
